import React, { useState } from 'react';
import {
  FiCamera,
  FiUsers,
  FiTrendingUp,
  FiBarChart2,
  FiMessageSquare,
} from 'react-icons/fi';
import ServiceDetail from './ServiceDetail';
import { useDarkMode } from '../context/DarkModeContext';
import { motion } from 'framer-motion';

const Services = () => {
  const { isDark } = useDarkMode();
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      icon: FiCamera,
      title: 'Strategic Growth',
      description:
        'Data-driven strategies to scale your brand presence across all social media platforms.',
      features: ['Daily Posting', 'Community Engagement', 'Growth Hacking'],
      detailed: 'Our strategic growth service focuses on sustainable, organic expansion of your social media presence. We analyze your current metrics, identify growth opportunities, and implement proven strategies to increase followers, engagement, and brand awareness across all platforms.',
    },
    {
      icon: FiUsers,
      title: 'Influence & Authority',
      description:
        'Build thought leadership and brand credibility through strategic influencer partnerships.',
      features: ['Influencer Outreach', 'Campaign Management', 'ROI Tracking'],
      detailed: 'Establish your brand as an industry authority through strategic influencer collaborations. We identify and partner with influencers aligned with your brand values, manage campaigns, and track measurable ROI to ensure every partnership drives real business results.',
    },
    {
      icon: FiMessageSquare,
      title: 'Smart Strategy',
      description:
        'Creative campaigns that turn engagement into conversions with data-backed creativity.',
      features: ['Campaign Design', 'Content Strategy', 'Conversion Optimization'],
      detailed: 'Transform your engagement into measurable conversions with our smart strategy service. We combine creative excellence with data-driven insights to design campaigns that resonate with your audience and drive meaningful business outcomes.',
    },
    {
      icon: FiTrendingUp,
      title: 'Community Management',
      description:
        'Engage, nurture, and grow your community with professional management and moderation.',
      features: ['24/7 Engagement', 'Sentiment Analysis', 'Trend Tracking'],
      detailed: 'Build a thriving community around your brand with expert management and moderation. We engage with your audience, handle customer service, analyze sentiment, and nurture relationships that turn followers into loyal advocates.',
    },
    {
      icon: FiBarChart2,
      title: 'Content Creation',
      description:
        'Professional content creation that resonates with your audience and drives engagement.',
      features: ['Video Production', 'Photography', 'Graphic Design'],
      detailed: 'High-quality content is the foundation of social media success. From stunning photography and videography to compelling copywriting and graphic design, we create content that captures attention, tells your story, and drives engagement.',
    },
    {
      icon: FiTrendingUp,
      title: 'Analytics & Reporting',
      description:
        'Comprehensive insights and detailed reports to measure success and optimize strategy.',
      features: ['Real-time Dashboards', 'Monthly Reports', 'ROI Tracking'],
      detailed: 'Data transparency is key to success. We provide comprehensive analytics, detailed monthly reports, competitor analysis, and actionable insights to help you understand what\'s working and optimize your strategy for maximum impact.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <section
      id="services"
      className={`relative w-full py-20 lg:py-32 transition-colors duration-500 ${
        isDark ? 'bg-black' : 'bg-light-secondary'
      }`}
    >
      {/* Background Decorations */}
      <motion.div
        className={`absolute top-32 left-0 w-80 h-80 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      ></motion.div>

      <motion.div
        className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 7, repeat: Infinity, delay: 1 }}
      ></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.p
            className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm mb-2"
            animate={{ letterSpacing: [0, '0.1em', 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Our Expertise
          </motion.p>
          <motion.h2
            className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
              isDark ? 'text-white' : 'text-black'
            }`}
            variants={itemVariants}
          >
            Our Services
          </motion.h2>
          <motion.p
            className={`font-poppins max-w-2xl mx-auto text-lg transition-colors ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            variants={itemVariants}
          >
            Comprehensive solutions to elevate your brand's social media presence
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={index}
                onClick={() => setSelectedService(service.title)}
                className={`group relative p-8 rounded-xl border cursor-pointer transition-all duration-300 overflow-hidden ${
                  isDark
                    ? 'bg-dark-secondary border-primary-gold/10 hover:border-primary-gold/60'
                    : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/60'
                }`}
                variants={itemVariants}
                whileHover={{
                  y: -12,
                  scale: 1.06,
                }}
                whileTap={{ scale: 0.94 }}
                style={{
                  boxShadow: 'inset 0 0 20px rgba(255, 215, 0, 0)',
                }}
                animate={{
                  boxShadow: 'inset 0 0 20px rgba(255, 215, 0, 0)',
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Enhanced Background Gradient on Hover */}
                <motion.div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                    isDark
                      ? 'bg-gradient-to-br from-primary-gold/12 to-primary-gold/2'
                      : 'bg-gradient-to-br from-primary-gold/15 to-primary-gold/2'
                  }`}
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                  }}
                  transition={{ duration: 6, repeat: Infinity }}
                ></motion.div>

                {/* Glow border effect */}
                <motion.div
                  className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-30 pointer-events-none transition-opacity duration-300 ${
                    isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/3'
                  }`}
                  animate={{
                    boxShadow: [
                      'inset 0 0 20px rgba(255, 215, 0, 0)',
                      'inset 0 0 40px rgba(255, 215, 0, 0.3)',
                      'inset 0 0 20px rgba(255, 215, 0, 0)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                ></motion.div>

                <div className="relative z-10">
                  {/* Icon Container */}
                  <motion.div
                    className={`inline-flex items-center justify-center h-16 w-16 rounded-lg transition-all mb-6 ${
                      isDark
                        ? 'bg-primary-gold/10 group-hover:bg-primary-gold/25'
                        : 'bg-primary-gold/15 group-hover:bg-primary-gold/30'
                    }`}
                    whileHover={{
                      scale: 1.2,
                      rotate: 10,
                    }}
                  >
                    <motion.div
                      animate={{
                        y: [0, -3, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.2,
                      }}
                    >
                      <IconComponent className="h-8 w-8 text-primary-gold" />
                    </motion.div>
                  </motion.div>

                  {/* Title */}
                  <motion.h3
                    className={`font-montserrat font-bold text-xl mb-3 transition-colors duration-300 ${
                      isDark ? 'text-white group-hover:text-primary-gold' : 'text-black group-hover:text-primary-gold'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {service.title}
                  </motion.h3>

                  {/* Description */}
                  <motion.p
                    className={`font-poppins text-sm mb-6 leading-relaxed transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    {service.description}
                  </motion.p>

                  {/* Features */}
                  <motion.div
                    className={`space-y-2 pt-4 border-t transition-colors ${
                      isDark ? 'border-primary-gold/10' : 'border-primary-gold/20'
                    }`}
                  >
                    {service.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-center gap-2"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-primary-gold"
                          animate={{
                            scale: [1, 1.3, 1],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: idx * 0.15,
                          }}
                        ></motion.div>
                        <span
                          className={`font-poppins text-sm transition-colors ${
                            isDark ? 'text-white' : 'text-gray-700'
                          }`}
                        >
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>

                {/* Animated Glow Background */}
                <motion.div
                  className={`absolute -bottom-1 -right-1 w-20 h-20 rounded-full blur-2xl transition-all duration-500 -z-10 ${
                    isDark ? 'bg-primary-gold/10 group-hover:bg-primary-gold/15' : 'bg-primary-gold/15 group-hover:bg-primary-gold/20'
                  }`}
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                ></motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="mt-16 text-center"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.p
            className={`font-poppins mb-6 text-lg transition-colors ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Ready to transform your social media strategy?
          </motion.p>
          <motion.button
            onClick={() => {
              const contactSection = document.getElementById('contact');
              contactSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`px-10 py-4 bg-primary-gold text-black font-montserrat font-bold rounded-lg transition-all duration-300 inline-block cursor-pointer ${
              isDark ? 'hover:shadow-gold-glow-lg' : 'hover:shadow-lg'
            }`}
            whileHover={{
              scale: 1.05,
              y: -4,
            }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule a Free Consultation
          </motion.button>
        </motion.div>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <ServiceDetail service={selectedService} onClose={() => setSelectedService(null)} />
      )}
    </section>
  );
};

export default Services;
